<template>
  <portlet
    :icon="'fad fa-clock'"
    :title="'Overwerk instellingen'"
    :footer="!isReadOnly"
  >
    <template slot="portletBody">
      <div v-if="isReadOnly" class="alert alert-info mb-3">
        Overwerk instellingen zijn alleen-lezen.
      </div>
      <div>
        <div v-if="loadingSettings" class="alert alert-info mb-3">
          Overwerk instellingen laden...
        </div>
        <div v-if="hasBlockingError" class="alert alert-danger mb-3">
          {{ blockingError }}
        </div>
        <template v-if="!loadingSettings && !hasBlockingError">
        <div class="row">
          <div class="form-group col-12">
            <label class="d-block">Overwerk berekenen op basis van</label>
            <div class="form-check form-check-inline">
              <input
                id="overtime-mode-per-day"
                class="form-check-input"
                type="radio"
                name="overtime-mode"
                :value="MODES.PER_DAY"
                v-model="editOvertimeSettings.mode"
                :disabled="isReadOnly"
                @change="onModeChange(MODES.PER_DAY)"
              />
              <label class="form-check-label" for="overtime-mode-per-day">Per dag</label>
            </div>
            <div class="form-check form-check-inline">
              <input
                id="overtime-mode-per-week"
                class="form-check-input"
                type="radio"
                name="overtime-mode"
                :value="MODES.PER_WEEK"
                v-model="editOvertimeSettings.mode"
                :disabled="isReadOnly"
                @change="onModeChange(MODES.PER_WEEK)"
              />
              <label class="form-check-label" for="overtime-mode-per-week">Per week</label>
            </div>
            <span v-if="validationErrors.mode" class="small text-danger d-block">{{ validationErrors.mode }}</span>
          </div>
        </div>

        <div class="row" v-if="editOvertimeSettings.mode">
          <div class="form-group col-lg-4">
            <label for="overtime-limit">{{ limitFieldLabel }}</label>
            <input
              id="overtime-limit"
              type="number"
              class="form-control"
              :min="limitRange.min"
              :max="limitRange.max"
              :step="limitStep"
              v-model="editOvertimeSettings.limit"
              :disabled="isReadOnly"
              @input="clearLimitError"
            />
            <span v-if="validationErrors.limit" class="small text-danger">{{ validationErrors.limit }}</span>
          </div>
        </div>

        <hr />

        <div
          class="overtime-day-card"
          v-for="dayNumber in ISO_DAY_NUMBERS"
          :key="dayNumber"
        >
          <div class="d-flex align-items-center justify-content-between mb-2">
            <h6 class="mb-0">{{ DAY_NAMES[dayNumber] }}</h6>
            <button
              v-if="!isReadOnly"
              type="button"
              class="btn btn-secondary btn-sm"
              @click="addEntry(dayNumber)"
            >
              <i class="fa fa-plus mr-1"></i> Toevoegen
            </button>
          </div>

          <div class="row text-muted small font-weight-bold">
            <div class="col-lg-3">Ploegentoeslag</div>
            <div class="col-lg-3">Overwerkpercentage</div>
            <div class="col-lg-5">Label</div>
            <div class="col-lg-1"></div>
          </div>

          <overtime-day-entry
            v-for="(entry, entryIndex) in getDayEntries(dayNumber)"
            :key="dayNumber + '-' + entryIndex"
            :entry="entry"
            :canRemove="getDayEntries(dayNumber).length > 0"
            :errors="getEntryErrors(dayNumber, entryIndex)"
            :disabled="isReadOnly"
            @remove="removeEntry(dayNumber, entryIndex)"
            @update-entry="updateEntry(dayNumber, entryIndex, $event)"
            @input-change="clearEntryErrors(dayNumber, entryIndex)"
          />

          <span
            v-if="validationErrors.days[dayNumber] && validationErrors.days[dayNumber].day"
            class="small text-danger d-block mt-1"
          >
            {{ validationErrors.days[dayNumber].day }}
          </span>
        </div>
        </template>
      </div>
    </template>

    <div slot="portletFooter" class="col text-right" v-if="!isReadOnly">
      <button
        @click="saveOvertimeSettings"
        type="button"
        class="btn btn-primary"
        :disabled="saving || loadingSettings || hasBlockingError"
      >
        <i :class="saving ? 'fal fa-spinner fa-spin mr-2' : 'fal fa-save mr-2'"></i>
        {{ saving ? 'Opslaan...' : 'Opslaan' }}
      </button>
    </div>
  </portlet>
</template>

<script>
import Vue from 'vue';
import { describeApiOutcome } from '@/utils/apiOutcome';
import { Can } from '../../../permission/ability';
import { RIGHTS } from '../../views/roles/rights';
import overtimeDayEntry from './overtimeDayEntry';
import {
  DAILY_LIMIT_MAX,
  DAILY_LIMIT_MIN,
  DAY_NAMES,
  ISO_DAY_NUMBERS,
  LABEL_MAX_LENGTH,
  LIMIT_STEP,
  MODES,
  PERCENTAGE_MAX,
  PERCENTAGE_MIN,
  WEEKLY_LIMIT_MAX,
  WEEKLY_LIMIT_MIN,
  createDefaultSettings,
  createEmptyEntry
} from './overtimeHelpers';

const duplicateErrorMessage = 'Combinatie ploegentoeslag + overwerkpercentage + label bestaat al op deze dag';
const PER_DAY_WEEKLY_HOURS = 168;
const PER_WEEK_DAILY_HOURS = 24;

const SAVING_IS_BLOCKED = 'Opslaan is geblokkeerd.';
const NO_LINKED_ORGANIZATION = 'Geen gekoppelde organisatie gevonden voor deze klant.';
const SHIFTS_COULD_NOT_BE_LOADED = 'Shifts konden niet worden geladen.';
const CUSTOMER_COULD_NOT_BE_LOADED = 'Klant kon niet worden geladen.';

const blocksSaving = (reason) => `${reason} ${SAVING_IS_BLOCKED}`;

const PERCENTAGE_FIELDS = [
  { name: 'shift_allowance_percentage', label: 'Ploegentoeslag' },
  { name: 'overtime_percentage', label: 'Overwerkpercentage' }
];

const validatePercentageField = (entry, field, label) => {
  const rawValue = entry[field];
  const parsedValue = Number(rawValue);

  if (rawValue === null || rawValue === '' || Number.isNaN(parsedValue)) {
    return { valid: false, error: `${label} is verplicht`, parsedValue: null };
  }

  if (parsedValue < PERCENTAGE_MIN || parsedValue > PERCENTAGE_MAX) {
    return {
      valid: false,
      error: `${label} moet een getal zijn tussen ${PERCENTAGE_MIN} en ${PERCENTAGE_MAX}`,
      parsedValue
    };
  }

  return { valid: true, error: '', parsedValue };
};

const ISO_DAY_TO_SHIFT_DAY = {
  1: 'mon',
  2: 'tue',
  3: 'wed',
  4: 'thu',
  5: 'fri',
  6: 'sat',
  7: 'sun'
};

const SHIFT_DAY_TO_ISO_DAY = {
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
  sun: 7
};

const DEFAULT_SHIFT_OPTIONS = {
  allow_comments: false,
  allow_reimbursement: false,
  is_net: false,
  overtime_message: null
};

const clone = (value) => JSON.parse(JSON.stringify(value));

const createEntryErrors = () => {
  const errors = {
    label: '',
    duplicate: ''
  };

  PERCENTAGE_FIELDS.forEach(({ name }) => {
    errors[name] = '';
  });

  return errors;
};

const createValidationErrors = (settings) => {
  const errors = {
    mode: '',
    limit: '',
    days: {}
  };

  ISO_DAY_NUMBERS.forEach((dayNumber) => {
    const entries = settings && settings.days && Array.isArray(settings.days[dayNumber])
      ? settings.days[dayNumber]
      : [];

    errors.days[dayNumber] = {
      day: '',
      entries: entries.map(() => createEntryErrors())
    };
  });

  return errors;
};

const toFiniteNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

export default {
  props: ['id', 'customer'],
  components: {
    overtimeDayEntry
  },
  data() {
    const defaultSettings = createDefaultSettings();

    ISO_DAY_NUMBERS.forEach((dayNumber) => {
      defaultSettings.days[dayNumber] = [];
    });

    return {
      MODES,
      DAY_NAMES,
      ISO_DAY_NUMBERS,
      limitStep: LIMIT_STEP,
      saving: false,
      loadingSettings: false,
      blockingError: '',
      organizationId: null,
      loadedShifts: [],
      isInitializingSettings: false,
      editOvertimeSettings: defaultSettings,
      savedOvertimeSettingsCopy: clone(defaultSettings),
      validationErrors: createValidationErrors(defaultSettings)
    };
  },
  computed: {
    isReadOnly() {
      return !Can(RIGHTS.CUSTOMER_EDIT);
    },
    hasBlockingError() {
      return Boolean(this.blockingError);
    },
    limitRange() {
      if (this.editOvertimeSettings.mode === MODES.PER_WEEK) {
        return { min: WEEKLY_LIMIT_MIN, max: WEEKLY_LIMIT_MAX };
      }

      return { min: DAILY_LIMIT_MIN, max: DAILY_LIMIT_MAX };
    },
    limitFieldLabel() {
      if (this.editOvertimeSettings.mode === MODES.PER_WEEK) {
        return 'Wekelijkse urenlimiet';
      }

      return 'Dagelijkse urenlimiet';
    }
  },
  methods: {
    createEmptySettings() {
      const settings = createDefaultSettings();

      ISO_DAY_NUMBERS.forEach((dayNumber) => {
        settings.days[dayNumber] = [];
      });

      return settings;
    },
    setSettingsSnapshot(settings) {
      this.isInitializingSettings = true;
      this.editOvertimeSettings = clone(settings);
      this.savedOvertimeSettingsCopy = clone(settings);
      this.validationErrors = createValidationErrors(this.editOvertimeSettings);
      this.$emit('unsavedChanges', false);

      this.$nextTick(() => {
        this.isInitializingSettings = false;
      });
    },
    createApiPayloadBase() {
      const userData = this.$store.getters.getUserData || {};

      return {
        ApiClient: Vue.prototype.$ApiClient,
        departments: userData.departments
      };
    },
    async resolveOrganizationId() {
      const outcome = await this.$store.dispatch('organizations/findByCustomerId', {
        ...this.createApiPayloadBase(),
        customerId: this.customer && this.customer.id
      });

      if (!outcome.ok) {
        this.blockingError = blocksSaving(outcome.message);
        return null;
      }

      if (!outcome.organization || !outcome.organization.id) {
        this.blockingError = blocksSaving(NO_LINKED_ORGANIZATION);
        return null;
      }

      return outcome.organization.id;
    },
    async fetchShiftsForOrganization(organizationId) {
      const response = await this.$store.dispatch('shifts/getAll', {
        ...this.createApiPayloadBase(),
        company_id: organizationId
      });

      const outcome = describeApiOutcome(response, {
        fallback: SHIFTS_COULD_NOT_BE_LOADED
      });

      if (!outcome.ok) {
        this.blockingError = blocksSaving(outcome.message);
        return null;
      }

      return outcome.data && Array.isArray(outcome.data.data)
        ? outcome.data.data
        : [];
    },
    normalizeShiftDays(rawDays) {
      let parsedDays = rawDays;

      if (typeof rawDays === 'string') {
        try {
          parsedDays = JSON.parse(rawDays);
        } catch (error) {
          parsedDays = {};
        }
      }

      if (!parsedDays || typeof parsedDays !== 'object') {
        parsedDays = {};
      }

      const normalizedDays = {};
      Object.keys(SHIFT_DAY_TO_ISO_DAY).forEach((dayKey) => {
        normalizedDays[dayKey] = Boolean(parsedDays[dayKey]);
      });

      return normalizedDays;
    },
    resolveDayNumberFromShift(days) {
      const selectedDayKeys = Object.keys(days).filter((dayKey) => days[dayKey]);

      if (!selectedDayKeys.length) {
        return {
          dayNumber: 1,
          hasAmbiguousMapping: true
        };
      }

      return {
        dayNumber: SHIFT_DAY_TO_ISO_DAY[selectedDayKeys[0]] || 1,
        hasAmbiguousMapping: selectedDayKeys.length !== 1
      };
    },
    inferModeAndLimit(shifts) {
      if (!shifts.length) {
        return {
          mode: null,
          limit: null,
          usedFallback: false
        };
      }

      const allPerDayPattern = shifts.every((shift) => toFiniteNumberOrNull(shift.weekly_hours) === PER_DAY_WEEKLY_HOURS);
      const allPerWeekPattern = shifts.every((shift) => toFiniteNumberOrNull(shift.daily_hours) === PER_WEEK_DAILY_HOURS);

      const fallbackFromFirstShift = () => {
        const firstShift = shifts[0];

        if (toFiniteNumberOrNull(firstShift.weekly_hours) === PER_DAY_WEEKLY_HOURS) {
          return {
            mode: MODES.PER_DAY,
            limit: toFiniteNumberOrNull(firstShift.daily_hours),
            usedFallback: true
          };
        }

        if (toFiniteNumberOrNull(firstShift.daily_hours) === PER_WEEK_DAILY_HOURS) {
          return {
            mode: MODES.PER_WEEK,
            limit: toFiniteNumberOrNull(firstShift.weekly_hours),
            usedFallback: true
          };
        }

        return {
          mode: MODES.PER_DAY,
          limit: toFiniteNumberOrNull(firstShift.daily_hours),
          usedFallback: true
        };
      };

      if (allPerDayPattern === allPerWeekPattern) {
        return fallbackFromFirstShift();
      }

      const mode = allPerDayPattern ? MODES.PER_DAY : MODES.PER_WEEK;
      const limitField = mode === MODES.PER_DAY ? 'daily_hours' : 'weekly_hours';
      const firstLimit = toFiniteNumberOrNull(shifts[0][limitField]);
      const hasSameLimit = shifts.every((shift) => toFiniteNumberOrNull(shift[limitField]) === firstLimit);

      if (!hasSameLimit) {
        return fallbackFromFirstShift();
      }

      return {
        mode,
        limit: firstLimit,
        usedFallback: false
      };
    },
    mapShiftsToSettings(shifts) {
      const settings = createDefaultSettings();
      const loadedShifts = [];
      let hasAmbiguousMapping = false;

      ISO_DAY_NUMBERS.forEach((dayNumber) => {
        settings.days[dayNumber] = [];
      });

      shifts.forEach((shift) => {
        if (!shift || !shift.id) {
          return;
        }

        const normalizedDays = this.normalizeShiftDays(shift.days);
        const { dayNumber, hasAmbiguousMapping: shiftMappingAmbiguous } = this.resolveDayNumberFromShift(normalizedDays);
        hasAmbiguousMapping = hasAmbiguousMapping || shiftMappingAmbiguous;

        settings.days[dayNumber].push({
          shift_allowance_percentage: toFiniteNumberOrNull(shift.shift_allowance_percentage),
          overtime_percentage: toFiniteNumberOrNull(shift.overtime_percentage),
          label: typeof shift.name === 'string' ? shift.name : '',
          shiftId: shift.id
        });

        loadedShifts.push({
          id: shift.id,
          name: typeof shift.name === 'string' ? shift.name : '',
          shift_allowance_percentage: toFiniteNumberOrNull(shift.shift_allowance_percentage),
          overtime_percentage: toFiniteNumberOrNull(shift.overtime_percentage),
          daily_hours: toFiniteNumberOrNull(shift.daily_hours),
          weekly_hours: toFiniteNumberOrNull(shift.weekly_hours),
          days: normalizedDays,
          allow_comments: Boolean(shift.allow_comments),
          allow_reimbursement: Boolean(shift.allow_reimbursement),
          is_net: Boolean(shift.is_net),
          overtime_message: typeof shift.overtime_message === 'string' ? shift.overtime_message : null
        });
      });

      const inferredModeAndLimit = this.inferModeAndLimit(loadedShifts);
      settings.mode = inferredModeAndLimit.mode;
      settings.limit = inferredModeAndLimit.limit;

      if ((inferredModeAndLimit.usedFallback || hasAmbiguousMapping) && loadedShifts.length) {
        this.$toasted.global.info({
          message: 'Historische shiftdata was niet volledig consistent. Eerste gevonden instelling is gebruikt.'
        });
      }

      return {
        settings,
        loadedShifts
      };
    },
    async initializeSettings() {
      this.loadingSettings = true;
      this.blockingError = '';
      this.organizationId = null;
      this.loadedShifts = [];
      this.setSettingsSnapshot(this.createEmptySettings());

      if (!this.customer || !this.customer.id) {
        this.blockingError = blocksSaving(CUSTOMER_COULD_NOT_BE_LOADED);
        this.loadingSettings = false;
        return;
      }

      const organizationId = await this.resolveOrganizationId();
      if (!organizationId) {
        this.loadingSettings = false;
        return;
      }

      this.organizationId = organizationId;
      const shifts = await this.fetchShiftsForOrganization(organizationId);

      if (!Array.isArray(shifts)) {
        this.loadingSettings = false;
        return;
      }

      const mappedData = this.mapShiftsToSettings(shifts);
      this.loadedShifts = clone(mappedData.loadedShifts);
      this.setSettingsSnapshot(mappedData.settings);
      this.loadingSettings = false;
    },
    getDayEntries(dayNumber) {
      if (!this.editOvertimeSettings.days[dayNumber]) {
        this.$set(this.editOvertimeSettings.days, dayNumber, []);
      }

      return this.editOvertimeSettings.days[dayNumber];
    },
    getEntryErrors(dayNumber, entryIndex) {
      if (!this.validationErrors.days[dayNumber] || !this.validationErrors.days[dayNumber].entries[entryIndex]) {
        return createEntryErrors();
      }

      return this.validationErrors.days[dayNumber].entries[entryIndex];
    },
    onModeChange(mode) {
      this.editOvertimeSettings.mode = mode;
      this.editOvertimeSettings.limit = null;
      this.validationErrors.mode = '';
      this.validationErrors.limit = '';
    },
    clearLimitError() {
      this.validationErrors.limit = '';
    },
    clearEntryErrors(dayNumber, entryIndex) {
      if (!this.validationErrors.days[dayNumber]) {
        return;
      }

      this.validationErrors.days[dayNumber].day = '';
      this.validationErrors.days[dayNumber].entries.forEach((entryErrors) => {
        entryErrors.duplicate = '';
      });

      if (this.validationErrors.days[dayNumber].entries[entryIndex]) {
        this.validationErrors.days[dayNumber].entries[entryIndex] = createEntryErrors();
      }
    },
    addEntry(dayNumber) {
      this.getDayEntries(dayNumber).push(createEmptyEntry());

      if (this.validationErrors.days[dayNumber]) {
        this.validationErrors.days[dayNumber].entries.push(createEntryErrors());
      }
    },
    removeEntry(dayNumber, entryIndex) {
      const dayEntries = this.getDayEntries(dayNumber);

      dayEntries.splice(entryIndex, 1);

      if (this.validationErrors.days[dayNumber]) {
        this.validationErrors.days[dayNumber].entries.splice(entryIndex, 1);
      }
    },
    updateEntry(dayNumber, entryIndex, payload) {
      const allowedFields = [...PERCENTAGE_FIELDS.map((field) => field.name), 'label'];

      if (!payload || !allowedFields.includes(payload.field)) {
        return;
      }

      const dayEntries = this.getDayEntries(dayNumber);

      if (!dayEntries[entryIndex]) {
        return;
      }

      this.$set(dayEntries[entryIndex], payload.field, payload.value);
    },
    validateForm() {
      const errors = createValidationErrors(this.editOvertimeSettings);
      let isValid = true;

      if (this.editOvertimeSettings.mode !== MODES.PER_DAY && this.editOvertimeSettings.mode !== MODES.PER_WEEK) {
        errors.mode = 'Selecteer een berekeningsmethode';
        isValid = false;
      }

      if (!errors.mode) {
        const limitNumber = Number(this.editOvertimeSettings.limit);

        if (this.editOvertimeSettings.limit === null || this.editOvertimeSettings.limit === '' || Number.isNaN(limitNumber)) {
          errors.limit = 'Urenlimiet is verplicht';
          isValid = false;
        } else if (limitNumber < this.limitRange.min || limitNumber > this.limitRange.max) {
          errors.limit = `Urenlimiet moet tussen ${this.limitRange.min} en ${this.limitRange.max} liggen`;
          isValid = false;
        }
      }

      ISO_DAY_NUMBERS.forEach((dayNumber) => {
        const dayEntries = this.getDayEntries(dayNumber);
        const duplicateMap = {};

        errors.days[dayNumber].entries = dayEntries.map(() => createEntryErrors());

        dayEntries.forEach((entry, entryIndex) => {
          const entryErrors = errors.days[dayNumber].entries[entryIndex];
          const labelValue = typeof entry.label === 'string' ? entry.label.trim() : '';
          const percentageResults = {};
          let allPercentagesValid = true;

          PERCENTAGE_FIELDS.forEach(({ name, label }) => {
            const result = validatePercentageField(entry, name, label);
            percentageResults[name] = result;

            if (!result.valid) {
              entryErrors[name] = result.error;
              allPercentagesValid = false;
              isValid = false;
            }
          });

          if (!labelValue) {
            entryErrors.label = 'Label is verplicht';
            isValid = false;
          } else if (labelValue.length > LABEL_MAX_LENGTH) {
            entryErrors.label = `Label mag maximaal ${LABEL_MAX_LENGTH} tekens bevatten`;
            isValid = false;
          }

          if (allPercentagesValid && !entryErrors.label) {
            const duplicateKey = PERCENTAGE_FIELDS
              .map(({ name }) => percentageResults[name].parsedValue)
              .concat(labelValue.toLowerCase())
              .join('::');

            if (duplicateMap[duplicateKey] === undefined) {
              duplicateMap[duplicateKey] = entryIndex;
            } else {
              entryErrors.duplicate = duplicateErrorMessage;
              errors.days[dayNumber].entries[duplicateMap[duplicateKey]].duplicate = duplicateErrorMessage;
              isValid = false;
            }
          }
        });
      });

      this.validationErrors = errors;

      return isValid;
    },
    buildDaysPayload(dayNumber) {
      const daysPayload = {
        mon: false,
        tue: false,
        wed: false,
        thu: false,
        fri: false,
        sat: false,
        sun: false
      };

      const selectedDayKey = ISO_DAY_TO_SHIFT_DAY[dayNumber];
      if (selectedDayKey) {
        daysPayload[selectedDayKey] = true;
      }

      return daysPayload;
    },
    buildDesiredShifts() {
      const desiredShifts = [];

      ISO_DAY_NUMBERS.forEach((dayNumber) => {
        this.getDayEntries(dayNumber).forEach((entry) => {
          desiredShifts.push({
            dayNumber,
            shiftId: entry && entry.shiftId ? entry.shiftId : null,
            label: typeof entry.label === 'string' ? entry.label.trim() : '',
            shift_allowance_percentage: Number(entry.shift_allowance_percentage),
            overtime_percentage: Number(entry.overtime_percentage)
          });
        });
      });

      return desiredShifts;
    },
    buildShiftPayload(entry, existingShift) {
      const isPerDay = this.editOvertimeSettings.mode === MODES.PER_DAY;
      const limitNumber = Number(this.editOvertimeSettings.limit);

      return {
        id: entry.shiftId || undefined,
        organization_id: this.organizationId,
        name: entry.label,
        shift_allowance_percentage: entry.shift_allowance_percentage,
        overtime_percentage: entry.overtime_percentage,
        days: this.buildDaysPayload(entry.dayNumber),
        daily_hours: isPerDay ? limitNumber : PER_WEEK_DAILY_HOURS,
        weekly_hours: isPerDay ? PER_DAY_WEEKLY_HOURS : limitNumber,
        allow_comments: existingShift ? Boolean(existingShift.allow_comments) : DEFAULT_SHIFT_OPTIONS.allow_comments,
        allow_reimbursement: existingShift ? Boolean(existingShift.allow_reimbursement) : DEFAULT_SHIFT_OPTIONS.allow_reimbursement,
        is_net: existingShift ? Boolean(existingShift.is_net) : DEFAULT_SHIFT_OPTIONS.is_net,
        overtime_message: existingShift && typeof existingShift.overtime_message === 'string'
          ? existingShift.overtime_message
          : DEFAULT_SHIFT_OPTIONS.overtime_message
      };
    },
    async reconcileShifts() {
      const desiredShifts = this.buildDesiredShifts();
      const loadedShiftMap = this.loadedShifts.reduce((result, shift) => {
        result[shift.id] = shift;
        return result;
      }, {});
      const shiftsPayload = desiredShifts.map((entry) => this.buildShiftPayload(entry, loadedShiftMap[entry.shiftId]));
      const apiPayloadBase = this.createApiPayloadBase();
      const response = await this.$store.dispatch('shifts/syncShifts', {
        ...apiPayloadBase,
        organization_id: this.organizationId,
        shifts: shiftsPayload
      });

      const outcome = describeApiOutcome(response, {fallback: 'Niet alle shifts konden worden opgeslagen.'});

      if (!outcome.ok) {
        throw new Error(outcome.message);
      }
    },
    async saveOvertimeSettings() {
      if (this.saving || this.loadingSettings || this.hasBlockingError) {
        return;
      }

      if (!this.organizationId) {
        this.$toasted.global.failedRequest({
          message: 'Geen organisatie gekoppeld aan deze klant. Opslaan is niet mogelijk.'
        });
        return;
      }

      if (!this.validateForm()) {
        this.$toasted.global.failedFormRequest();
        return;
      }

      this.saving = true;

      try {
        await this.reconcileShifts();
        await this.initializeSettings();
        this.$toasted.global.successRequest({ message: 'Overwerk instellingen opgeslagen' });
      } catch (error) {
        this.$toasted.global.failedRequest({
          message: error && error.message
            ? error.message
            : 'Overwerk instellingen konden niet worden opgeslagen.'
        });
      } finally {
        this.saving = false;
      }
    }
  },
  watch: {
    editOvertimeSettings: {
      handler: function (value) {
        if (this.isInitializingSettings) {
          return;
        }

        this.$emit('unsavedChanges', JSON.stringify(value) !== JSON.stringify(this.savedOvertimeSettingsCopy));
      },
      deep: true
    },
    customer: {
      handler: function () {
        this.initializeSettings();
      },
      deep: false
    }
  },
  created() {
    this.initializeSettings();
  }
};
</script>

<style lang="scss" scoped>
.overtime-day-card {
  border: 1px solid #e7e7e7;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 12px;
}
</style>
